import { FileText, User, LogOut, Menu, X } from "lucide-react";
import { menuItems } from "../../pages/User/Dashboard";
import { useViewMaterial } from "@/hooks/viewMaterial";
import { toTitleCase } from "@/utils/string";

const ClassDetail: React.FC = () => {
  const {
    isSidebarOpen,
    isCollapsed,
    user,
    groupedMaterials,
    activeMaterial,
    loading,
    course,
    navigate,
    setIsCollapsed,
    setIsSidebarOpen,
    handleLogout,
    handleSidebarClick,
    setActiveMaterial,
  } = useViewMaterial();

  const renderContent = (text: string) => {
    return text.split("\n").map((str, index) => (
      <p key={index} className="mb-4 leading-relaxed text-gray-700">
        {str}
      </p>
    ));
  };

  return (
    <div className="flex min-h-screen bg-white font-sans text-gray-900 relative">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 bottom-0 left-0 z-50 bg-white border-r border-gray-100 flex flex-col 
          transform transition-all duration-300 ease-in-out overflow-hidden
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          lg:translate-x-0 lg:static lg:h-screen lg:inset-y-0
          ${isCollapsed ? "lg:w-[80px]" : "lg:w-64"} 
        `}
      >
        {/* HEADER SIDEBAR */}
        <div
          className={`
            h-20 flex items-center mb-2 transition-all duration-300 w-full
            ${isCollapsed ? "justify-center px-0" : "justify-between px-6"}
          `}
        >
          {!isCollapsed && (
            <img
              src="/Fluenesia.png"
              alt="Fluenesia Logo"
              className="h-6 w-auto object-contain block"
            />
          )}

          <button
            onClick={() => {
              if (window.innerWidth < 1024) setIsSidebarOpen(false);
              else setIsCollapsed(!isCollapsed);
            }}
            className={`
              text-gray-600 hover:text-[#B21318] transition rounded-lg hover:bg-gray-100 focus:outline-none flex items-center justify-center p-2
              ${!isCollapsed ? "mr-2" : ""}
            `}
          >
            <div className="lg:hidden">
              <X size={24} />
            </div>
            <div className="hidden lg:block">
              <Menu size={24} strokeWidth={2.5} />
            </div>
          </button>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-4">
          <p
            className={`text-[10px] font-bold text-black uppercase mb-2 mt-4 tracking-wider transition-all duration-300 ${isCollapsed ? "text-center text-[9px]" : "px-2"}`}
          >
            Dashboard
          </p>

          {/* MENU ITEMS IMPORTED DARI DASHBOARD */}
          {menuItems.map((item) => (
            <button
              key={item.name}
              onClick={() => handleSidebarClick(item.name)}
              title={isCollapsed ? item.name : ""}
              className={`
                group w-full flex items-center py-3 rounded-2xl font-medium transition-all duration-300 relative
                ${isCollapsed ? "justify-center px-0" : "justify-start px-4 gap-3"}
                ${item.name === "My Class" ? "bg-[#FF0000] text-white shadow-md shadow-red-200" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"}
              `}
            >
              <div className="shrink-0">{item.icon}</div>
              <span
                className={`text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 hidden" : "w-auto opacity-100 block"}`}
              >
                {item.name}
              </span>
              {isCollapsed && (
                <div className="absolute left-14 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                  {item.name}
                </div>
              )}
            </button>
          ))}

          <div className="pt-6">
            <p
              className={`text-[10px] font-bold text-black uppercase mb-2 tracking-wider transition-all duration-300 ${isCollapsed ? "text-center text-[9px]" : "px-2"}`}
            >
              Account
            </p>
            <button
              onClick={() => navigate("/user/dashboard")}
              title={isCollapsed ? "Profile" : ""}
              className={`
                group w-full flex items-center py-3 rounded-2xl font-medium transition-all duration-300
                ${isCollapsed ? "justify-center px-0" : "justify-start px-4 gap-3"}
                text-gray-400 hover:bg-gray-50 hover:text-gray-600
              `}
            >
              <div className="shrink-0">
                <User size={20} />
              </div>
              <span
                className={`text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 hidden" : "w-auto opacity-100 block"}`}
              >
                Profile
              </span>
            </button>
            <button
              onClick={handleLogout}
              title={isCollapsed ? "Logout" : ""}
              className={`
                w-full flex items-center py-3 rounded-2xl font-medium text-red-600 mt-2 hover:bg-red-50 transition-all duration-300
                ${isCollapsed ? "justify-center px-0" : "justify-start px-4 gap-3"}
              `}
            >
              <div className="shrink-0">
                <LogOut size={20} />
              </div>
              <span
                className={`text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 hidden" : "w-auto opacity-100 block"}`}
              >
                Logout
              </span>
            </button>
          </div>
        </nav>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main
        className={`flex-1 w-full bg-white min-h-screen transition-all duration-300 ${isCollapsed ? "lg:ml-[80px]" : "lg:ml-auto"}`}
      >
        {/* HEADER */}
        <header className="flex justify-between lg:justify-end p-4 lg:p-6 items-center sticky top-0 bg-white/90 backdrop-blur-sm z-30 border-b lg:border-none border-gray-100">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden"
          >
            <Menu size={24} />
          </button>
          {user && (
            <div className="flex items-center gap-3 px-2 lg:px-4 py-2 rounded-full hover:bg-gray-50 transition-all cursor-pointer">
              <div className="flex flex-col items-end leading-tight">
                <span className="font-bold text-sm text-gray-800 text-right whitespace-nowrap">
                  {user.name}
                </span>
                <span className="text-[10px] text-gray-500 uppercase font-medium">
                  {user.role || "Student"}
                </span>
              </div>
              <div className="w-9 h-9 bg-[#B21318] rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm uppercase shrink-0">
                {user.name.charAt(0)}
              </div>
            </div>
          )}
        </header>

        <div className="max-w-7xl mx-auto px-4 lg:px-8 pb-12">
          {/* Title Header */}
          <div className="flex gap-20 mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Course Materials
            </h1>
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm font-medium mt-2 text-gray-500">
              <span
                className="text-[#FF0000] cursor-pointer hover:underline"
                onClick={() =>
                  navigate("/user/dashboard", { state: { tab: "My Class" } })
                }
              >
                My Class
              </span>
              <span className="text-[#FF0000]"> ▸ </span>
              {course && (
                <span
                  className="text-[#FF0000] cursor-pointer hover:underline"
                  onClick={() => navigate(`/user/class/${course.id}`)}
                >
                  {toTitleCase(course.title)}
                </span>
              )}
              <span className="text-[#FF0000]"> ▸ </span>
              <span className="text-gray-900"> Course Materials</span>
            </div>
          </div>

          {/* CONTENT LAYOUT */}
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            {/* LEFT COLUMN: NAVIGATION (Modules) */}
            <div className="relative w-full lg:w-1/4 flex-shrink-0">
              <div
                className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 z-10
    bg-gradient-to-t from-white via-white/60 to-transparent rounded-b-2xl"
              />

              <div
                className="space-y-6 max-h-44 lg:max-h-96 overflow-y-auto px-4 py-4
      [&::-webkit-scrollbar]:w-1.5
      [&::-webkit-scrollbar-track]:bg-transparent
      [&::-webkit-scrollbar-thumb]:bg-[#FFE5E7]
      [&::-webkit-scrollbar-thumb]:rounded-full
      [&::-webkit-scrollbar]:hover:cursor-pointer
      scroll-smooth
    "
              >
                {loading ? (
                  <div className="text-center py-4 text-gray-400">
                    Loading modules...
                  </div>
                ) : groupedMaterials.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded-xl text-center text-gray-500 text-sm">
                    Belum ada materi untuk kelas ini.
                  </div>
                ) : (
                  groupedMaterials.map((week, index) => (
                    <div key={index}>
                      <div className="bg-[#FF0000] text-white font-bold py-3 px-6 rounded-2xl mb-4 shadow-sm text-center">
                        {week.weekTitle}
                      </div>
                      <div className="space-y-3">
                        {week.items.map((item) => {
                          const isActive = activeMaterial?.id === item.id;
                          return (
                            <div
                              key={item.id}
                              onClick={() => setActiveMaterial(item)}
                              className={`
                    cursor-pointer rounded-2xl p-4 border transition-all duration-200
                    ${
                      isActive
                        ? "bg-[#FFE5E7] border-[#FFCDD2] shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    }
                  `}
                            >
                              <p
                                className={`text-[10px] mb-1 font-medium ${isActive ? "text-red-500" : "text-gray-400"}`}
                              >
                                {item.material_type}
                              </p>
                              <p
                                className={`font-bold text-sm leading-tight ${isActive ? "text-[#B21318]" : "text-gray-800"}`}
                              >
                                {item.title}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: CONTENT DETAIL */}
            <div className="flex-1 w-full bg-white min-h-[400px]">
              {loading ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  Loading content...
                </div>
              ) : activeMaterial ? (
                <>
                  <div className="mb-6 border-b border-gray-100 pb-4">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-wide mb-1">
                      {activeMaterial.week_title} •{" "}
                      {activeMaterial.material_type}
                    </p>
                    <h2 className="text-2xl lg:text-3xl font-bold text-[#FF0000]">
                      {activeMaterial.title}
                    </h2>
                  </div>
                  <div className="text-gray-800 text-base leading-relaxed">
                    {renderContent(activeMaterial.content)}
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 py-10">
                  <FileText size={48} className="mb-2 opacity-20" />
                  <p>Pilih materi di menu sebelah kiri untuk melihat detail.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClassDetail;
